import { UseMutationResult } from '@tanstack/react-query';
import { FieldValues, UseFormSetError } from 'react-hook-form';

import {
	ApplicationApi,
	CreateEndCustomerCommand,
	EndCustomerDto,
	OwnerAndUnitManagerDto,
} from 'api';
import { IsExternal } from 'library/types';
import { ChangedByInfoDisplayProps } from 'modules/UnitOverview/Components/ChangedByInfoDisplay';

export interface EndCustomerWithLanguageDto extends EndCustomerDto {
	notificationLanguage?: string;
}

export type EndCustomerSearchResponse = {
	results: EndCustomerWithLanguageDto[];
	hasMoreResults: boolean;
};

export type CustomerFormSubmitType<T = EndCustomerWithLanguageDto> = {
	onSuccess: (data: T) => void;
	onFailure?: () => void;
	setError: UseFormSetError<T & FieldValues>;
};
export type EndCustomerFormSubmitHookType = (
	params: CustomerFormSubmitType
) => {
	handleFormSubmit: (data: EndCustomerWithLanguageDto) => Promise<void>;
	handleFormInvalid: () => void;
	customerMutation: UseMutationResult<
		EndCustomerWithLanguageDto,
		unknown,
		CreateEndCustomerCommand,
		unknown
	>;
};
export type EndCustomerBoxProps = {
	customer: EndCustomerWithLanguageDto | null | undefined;
	isOwner: boolean;
};
export type EndCustomerBoxType = (
	props: EndCustomerBoxProps
) => React.JSX.Element;
export type DeleteCustomerDialogProps = {
	customer: EndCustomerWithLanguageDto;
	onClose: (
		customer: EndCustomerWithLanguageDto,
		wasDeleted: boolean
	) => void;
};

export interface EditEndCustomerProps {
	onEditCustomer: (customer: EndCustomerWithLanguageDto) => void;
	unitId: number;
}
export type ConnectedEndCustomerCardDisplayProps = EndCustomerBoxProps &
	EditEndCustomerProps &
	ChangedByInfoDisplayProps &
	IsExternal;

export type ConnectedEndCustomerCardDisplayType = (
	props: ConnectedEndCustomerCardDisplayProps
) => React.JSX.Element;

export type UnitConnectedCustomersQuery = (
	api: ApplicationApi,
	id: number
) => {
	queryKey: readonly ['unit', number, 'connected-customers'];
	queryFn: () => Promise<OwnerAndUnitManagerDto>;
};

export type SearchFormData = {
	email?: string | null;
	companyName?: string | null;
	vatNumber?: string | null;
	phonePrefix?: string | null;
	phoneNumber?: string | null;
	firstName?: string | null;
	surNameOrLastName?: string | null;
	county?: string | null;
	city?: string | null;
	zipCode?: string | null;
	address?: string | null;
	countryCode?: string | null;
	resultsCount?: number | null;
};

// type is simplified and some fields are skipped as they are not needed for search - can be extended in the future if needed
// endCustomerIds, title, addressSecondPart
export type SearchEndCustomersQueryData = {
	resultsCount?: number | null;
	firstName?: string | null;
	surname?: string | null;
	addressFirstPart?: string | null;
	city?: string | null;
	county?: string | null;
	countryCode?: string | null;
	companyName?: string | null;
	email?: string | null;
	phone?: string | null;
	phonePrefixCountryCode?: string | null;
	postalCode?: string | null;
	vatNumber?: string | null;
};

export const MARKETING_CONSENT_LEGAL_REFERENCE =
	'1997b880-a2f5-46db-854f-1a9cc4e244f2';
export const STORAGE_CONSENT_LEGAL_REFERENCE =
	'02ada8d6-9f0b-454d-95b8-20b07941de04';
